import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Spinner, Alert } from 'react-bootstrap';
import { FiArrowLeft, FiCalendar, FiMapPin, FiDollarSign, FiClock, FiGlobe, FiUser, FiBriefcase } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { useDispatch, useSelector } from 'react-redux';
import { fetchActivityByIdRequest } from '../../store/actions/dataActions';
import './AdminActivityDetail.css';

// Helper function to get nested values
const getDeep = (obj, path, defaultValue = null) => {
    const keys = Array.isArray(path) ? path : path.split('.');
    let result = obj;
    for (const key of keys) {
        if (result == null || typeof result !== 'object') return defaultValue;
        result = result[key];
    }
    return result !== undefined && result !== null ? result : defaultValue;
};

const Label = ({ children }) => (
    <div className="detail-label" style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '8px', fontWeight: 500 }}>
        {children}
    </div>
);

const Value = ({ children, style }) => (
    <div className="detail-value" style={{ fontWeight: 600, color: 'var(--text-primary)', minHeight: '24px', ...style }}>
        {children || <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>N/A</span>}
    </div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const AdminActivityDetail = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { activityId } = useParams();
    const { selectedActivity } = useSelector((state) => state.data);

    useEffect(() => {
        if (activityId) {
            dispatch(fetchActivityByIdRequest(activityId));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activityId]);

    const activityInfo = selectedActivity?.data?.activity || selectedActivity?.data || null;
    const partnerInfo = activityInfo?.partner_info || {};
    const references = activityInfo?.references || [];

    if (selectedActivity.loading) {
        return (
            <div className="dashboard-page admin-activity-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de l'activité</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/activity/activities')}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement de l'activité...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (selectedActivity.error) {
        return (
            <div className="dashboard-page admin-activity-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de l'activité</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/activity/activities')}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">{selectedActivity.error}</Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!activityInfo) {
        return (
            <div className="dashboard-page admin-activity-detail-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail de l'activité</h2>
                            <Button variant="outline-secondary" onClick={() => navigate('/activity/activities')}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée d'activité disponible pour l'identifiant: {activityId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    return (
        <div className="dashboard-page admin-activity-detail-page">
            <Card>
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h2 className="page-title">
                                <FiCalendar className="me-2" />
                                Détail de l'activité
                            </h2>
                            <p className="text-muted mb-0">Code activité: {getDeep(activityInfo, 'activity_code', 'N/A')}</p>
                        </div>
                        <Button variant="outline-secondary" onClick={() => navigate('/activity/activities')}>
                            <FiArrowLeft className="me-2" /> Retour
                        </Button>
                    </div>

                    {/* Informations principales */}
                    <Section title="Informations principales">
                        <Row>
                            <Col md={6}>
                                <Label>Code activité</Label>
                                <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{getDeep(activityInfo, 'activity_code', 'N/A')}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Nom</Label>
                                <Value>{getDeep(activityInfo, 'name', 'N/A')}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Catégorie</Label>
                                <Value>
                                    <Badge bg="info">{getDeep(activityInfo, 'category', 'N/A')}</Badge>
                                </Value>
                            </Col>
                            <Col md={6}>
                                <Label>Durée</Label>
                                <Value>{getDeep(activityInfo, 'duration', 'N/A')}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                            </Col>
                            <Col md={6}>
                                <Label>Prix</Label>
                                <Value>{getDeep(activityInfo, 'activity_price', 'N/A')}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Numéro de réservation</Label>
                                <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{getDeep(activityInfo, 'reservation_number', 'N/A')}</Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Dates et heures */}
                    <Section title="Dates et heures">
                        <Row>
                            <Col md={6}>
                                <Label>Date de début</Label>
                                <Value>{getDeep(activityInfo, 'activity_start_date', 'N/A')}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Date de fin</Label>
                                <Value>{getDeep(activityInfo, 'activity_end_date', 'N/A')}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Heure de début</Label>
                                <Value>{getDeep(activityInfo, 'activity_start_time', 'N/A')}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Heure de fin</Label>
                                <Value>{getDeep(activityInfo, 'activity_end_time', 'N/A')}</Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Adresse */}
                    <Section title="Adresse">
                        <Row>
                            <Col md={12}>
                                <Label>Adresse</Label>
                                <Value>{getDeep(activityInfo, 'activity_address', 'N/A')}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={4}>
                                <Label>Ville</Label>
                                <Value>{getDeep(activityInfo, 'activity_city', 'N/A')}</Value>
                            </Col>
                            <Col md={4}>
                                <Label>Code postal</Label>
                                <Value>{getDeep(activityInfo, 'activity_zip_code', 'N/A')}</Value>
                            </Col>
                            <Col md={4}>
                                <Label>Pays</Label>
                                <Value>{getDeep(activityInfo, 'activity_country', 'N/A')}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={6}>
                                <Label>Latitude</Label>
                                <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{getDeep(activityInfo, 'latitude', 'N/A')}</Value>
                            </Col>
                            <Col md={6}>
                                <Label>Longitude</Label>
                                <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{getDeep(activityInfo, 'longitude', 'N/A')}</Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations fournisseur */}
                    <Section title="Informations fournisseur">
                        <Row>
                            <Col md={6}>
                                <Label>Nom du fournisseur</Label>
                                <Value>{getDeep(activityInfo, 'supplier_name', 'N/A')}</Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={12}>
                                <Label>Adresse</Label>
                                <Value>
                                    <span style={{ whiteSpace: 'pre-line' }}>{getDeep(activityInfo, 'supplier_address', 'N/A')}</span>
                                </Value>
                            </Col>
                        </Row>
                        <Row className="mt-3">
                            <Col md={4}>
                                <Label>Ville</Label>
                                <Value>{getDeep(activityInfo, 'supplier_city', 'N/A')}</Value>
                            </Col>
                            <Col md={4}>
                                <Label>Code postal</Label>
                                <Value>{getDeep(activityInfo, 'supplier_zip_code', 'N/A')}</Value>
                            </Col>
                            <Col md={4}>
                                <Label>Pays</Label>
                                <Value>{getDeep(activityInfo, 'supplier_country', 'N/A')}</Value>
                            </Col>
                        </Row>
                    </Section>

                    {/* Informations partenaire */}
                    {partnerInfo && Object.keys(partnerInfo).length > 0 && (
                        <Section title="Informations partenaire">
                            <Row>
                                <Col md={6}>
                                    <Label>Nom</Label>
                                    <Value>{getDeep(partnerInfo, 'first_name', '')} {getDeep(partnerInfo, 'last_name', '')}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Entreprise</Label>
                                    <Value>{getDeep(partnerInfo, 'company_name', 'N/A')}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Email</Label>
                                    <Value>{getDeep(partnerInfo, 'email', 'N/A')}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Téléphone</Label>
                                    <Value>{getDeep(partnerInfo, 'phone_number', 'N/A')}</Value>
                                </Col>
                            </Row>
                            <Row className="mt-3">
                                <Col md={6}>
                                    <Label>Code utilisateur</Label>
                                    <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{getDeep(partnerInfo, 'user_code', 'N/A')}</Value>
                                </Col>
                                <Col md={6}>
                                    <Label>Code pays</Label>
                                    <Value>
                                        <Badge bg="primary">{getDeep(partnerInfo, 'country_code', 'N/A')}</Badge>
                                    </Value>
                                </Col>
                            </Row>
                        </Section>
                    )}

                    {/* Références */}
                    {references && references.length > 0 && (
                        <Section title="Références">
                            {references.map((reference, index) => (
                                <div key={getDeep(reference, 'reference_id', index)} className={index > 0 ? 'mt-4' : ''}>
                                    <Row>
                                        <Col md={6}>
                                            <Label>Code référence</Label>
                                            <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>{getDeep(reference, 'reference_code', 'N/A')}</Value>
                                        </Col>
                                        <Col md={6}>
                                            <Label>Nom</Label>
                                            <Value>{getDeep(reference, 'name', 'N/A')}</Value>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md={6}>
                                            <Label>Catégorie</Label>
                                            <Value>
                                                <Badge bg="info">{getDeep(reference, 'category', 'N/A')}</Badge>
                                            </Value>
                                        </Col>
                                        <Col md={6}>
                                        </Col>
                                    </Row>
                                    <Row className="mt-3">
                                    </Row>
                                    <Row className="mt-3">
                                        <Col md={6}>
                                            <Label>Déclenchement cumulé</Label>
                                            <Value>
                                                <Badge bg={getDeep(reference, 'prcp_is_cumulated', false) ? 'success' : 'secondary'}>
                                                    {getDeep(reference, 'prcp_is_cumulated', false) ? 'Oui' : 'Non'}
                                                </Badge>
                                            </Value>
                                        </Col>
                                        <Col md={6}>
                                            <Label>Référence visible</Label>
                                            <Value>
                                                <Badge bg={getDeep(reference, 'is_visible_reference', false) ? 'success' : 'secondary'}>
                                                    {getDeep(reference, 'is_visible_reference', false) ? 'Oui' : 'Non'}
                                                </Badge>
                                            </Value>
                                        </Col>
                                    </Row>

                                    {/* Sous-couvertures */}
                                    {getDeep(reference, 'subcoverages', []) && getDeep(reference, 'subcoverages', []).length > 0 && (
                                        <div className="mt-4">
                                            <h6 className="mb-3">Sous-couvertures</h6>
                                            {getDeep(reference, 'subcoverages', []).map((subcoverage, subIndex) => (
                                                <Card key={getDeep(subcoverage, 'sub_coverage_id', subIndex)} className="mb-3" style={{ backgroundColor: 'var(--bg-primary)' }}>
                                                    <Card.Body>
                                                        <Row>
                                                            <Col md={6}>
                                                                <Label>Code couverture</Label>
                                                                <Value style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{getDeep(subcoverage, 'coverage_code', 'N/A')}</Value>
                                                            </Col>
                                                            <Col md={6}>
                                                                <Label>Type de déclenchement</Label>
                                                                <Value>{getDeep(subcoverage, 'trigger_type', 'N/A')}</Value>
                                                            </Col>
                                                        </Row>
                                                        <Row className="mt-3">
                                                            <Col md={6}>
                                                                <Label>Date début</Label>
                                                                <Value>{getDeep(subcoverage, 'sub_coverage_start_date', 'N/A')}</Value>
                                                            </Col>
                                                            <Col md={6}>
                                                                <Label>Date fin</Label>
                                                                <Value>{getDeep(subcoverage, 'sub_coverage_end_date', 'N/A')}</Value>
                                                            </Col>
                                                        </Row>
                                                        <Row className="mt-3">
                                                            <Col md={4}>
                                                                <Label>Trigger bas</Label>
                                                                <Value>{getDeep(subcoverage, 'trigger_low', 'N/A')}</Value>
                                                            </Col>
                                                            <Col md={4}>
                                                                <Label>Trigger sup</Label>
                                                                <Value>{getDeep(subcoverage, 'trigger_sup', 'N/A')}</Value>
                                                            </Col>
                                                            <Col md={4}>
                                                                <Label>Unité</Label>
                                                                <Value>{getDeep(subcoverage, 'unit', 'N/A')}</Value>
                                                            </Col>
                                                        </Row>
                                                        <Row className="mt-3">
                                                            <Col md={6}>
                                                                <Label>SIV ratio maintenu</Label>
                                                                <Value>{getDeep(subcoverage, 'siv_ratio_maintained', 'N/A')}</Value>
                                                            </Col>
                                                            <Col md={6}>
                                                                <Label>SIV ratio annulé</Label>
                                                                <Value>{getDeep(subcoverage, 'siv_ratio_cancelled', 'N/A')}</Value>
                                                            </Col>
                                                        </Row>
                                                        <Row className="mt-3">
                                                            <Col md={6}>
                                                                <Label>Source de données</Label>
                                                                <Value>{getDeep(subcoverage, 'data_source', 'N/A')}</Value>
                                                            </Col>
                                                            <Col md={6}>
                                                                <Label>Ratio prix Orakle</Label>
                                                                <Value>{getDeep(subcoverage, 'orakle_price_ratio_excl_tsca', 'N/A')}</Value>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </Section>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default AdminActivityDetail;

